import apiClient from '@/api/index.js'

const state = {
  transactions: [],
  egresos: [],
  transaction: null,
  loading: false,
  error: null,
  pendingTransactions: [],
  orders: [],
  consolidations: [],
  shipments: [],
  loadingDocks: []
}

const getters = {
  allTransactions: state => state.transactions,
  allIngresos: state => state.transactions.filter(transaction => transaction.type === 'reception'),
  allEgresos: state => state.egresos,
  transactionById: state => id => state.transactions.find(transaction => transaction.id === id),
  loading: state => state.loading,
  pendingTransactions: state => state.pendingTransactions,
  allOrders: state => state.orders,
  allConsolidations: state => state.consolidations,
  allShipments: state => state.shipments,
  allLoadingDocks: state => state.loadingDocks,
  pickedOrders: state => state.orders.filter(order => 
    ['picked', 'shipped', 'consolidated', 'assigned_to_dock'].includes(order.status)
  ),
  availableConsolidations: state => state.consolidations.filter(consolidation => !consolidation.assigned_dock)
}

const actions = {
  fetchTransactions({ commit }) {
    commit('SET_LOADING', true)
    apiClient.getTransactions()
      .then(response => {
        commit('SET_TRANSACTIONS', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching transactions:', error)
      })
  },
  fetchIngresos({ commit }) {
    commit('SET_LOADING', true)
    apiClient.getTransactions()
      .then(response => {
        commit('SET_TRANSACTIONS', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching ingresos:', error)
      })
  },
  fetchEgresos({ commit }) {
    commit('SET_LOADING', true)
    apiClient.getEgresos()
      .then(response => {
        commit('SET_EGRESOS', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching egresos:', error)
      })
  },
  fetchTransaction({ commit }, id) {
    commit('SET_LOADING', true)
    apiClient.getTransaction(id)
      .then(response => {
        commit('SET_TRANSACTION', response.data)
        commit('SET_LOADING', false)
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching transaction:', error)
      })
  },
  createTransaction({ commit }, transaction) {
    commit('SET_LOADING', true)
    return apiClient.createTransaction(transaction)
      .then(response => {
        const newTransaction = response.data.transaction || response.data
        commit('ADD_TRANSACTION', newTransaction)
        commit('SET_LOADING', false)
        return newTransaction
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  
  // Crear recepción simplificada (directa al inventario)
  createSimplifiedReception({ commit }, receptionData) {
    commit('SET_LOADING', true)
    
    // Preparar datos para el endpoint simplificado
    const transactionData = {
      reference: receptionData.reference,
      supplier: receptionData.supplier,
      notes: receptionData.notes,
      details: receptionData.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        lot_number: item.lot_number,
        serial_number: item.serial_number,
        expiry_date: item.expiry_date,
        destination_location_id: item.destination_location_id,  // Opcional
        // Campos de dispositivos médicos
        forma_adquisicion: item.forma_adquisicion || null,
        fecha_recepcion_conforme: item.fecha_recepcion_conforme || null,
        numero_registro_sanitario: item.numero_registro_sanitario || null,
        numero_inventario_activo_fijo: item.numero_inventario_activo_fijo || null
      }))
    }
    
    console.log('🚀 Creando recepción simplificada:', transactionData)
    
    return apiClient.post('/transactions/simplified-reception', transactionData)
      .then(response => {
        const result = response.data
        const newTransaction = result.transaction
        
        // Agregar transacción al store
        commit('ADD_TRANSACTION', newTransaction)
        commit('SET_LOADING', false)
        
        console.log('✅ Recepción simplificada creada:', {
          transaction: newTransaction.reference,
          details: result.details.length,
          inventoryUpdated: result.inventory_updated
        })
        
        return result
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error en recepción simplificada:', error)
        return Promise.reject(error)
      })
  },
  updateTransaction({ commit, dispatch }, { id, data }) {
    console.log('=== DEBUG STORE updateTransaction ===')
    console.log('id:', id)
    console.log('data:', data)
    console.log('data.details:', data.details)
    
    commit('SET_LOADING', true)
    return apiClient.updateTransaction(id, data)
      .then(response => {
        const updatedTransaction = response.data.transaction || response.data
        commit('UPDATE_TRANSACTION', { id, ...updatedTransaction })
        
        // Si el estado cambió a 'Completado', procesar automáticamente la transacción
        if (data.status === 'Completado') {
          // El backend ya procesa automáticamente cuando se actualiza a 'Completado'
          // pero podemos refrescar los datos para asegurar que el frontend esté actualizado
          dispatch('fetchTransactions')
        }
        
        commit('SET_LOADING', false)
        return updatedTransaction
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  deleteTransaction({ commit }, id) {
    commit('SET_LOADING', true)
    return apiClient.deleteTransaction(id)
      .then(response => {
        commit('REMOVE_TRANSACTION', id)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  // La acción de completar la transacción se mapea al endpoint de process
  completeTransaction({ commit }, transactionId) {
    commit('SET_LOADING', true)
    return apiClient.processTransaction(transactionId)
      .then(response => {
        // Dependiendo de la respuesta, puede que quieras actualizar la transacción en el estado
        // Por ejemplo, se podría volver a llamar a fetchTransaction o actualizar el estado localmente
        commit('UPDATE_TRANSACTION', { id: transactionId, status: 'completed' })
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  cancelTransaction({ commit }, transactionId) {
    commit('SET_LOADING', true)
    return apiClient.cancelTransaction(transactionId)
      .then(response => {
        commit('UPDATE_TRANSACTION', { id: transactionId, status: 'cancelled' })
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  async getPendingTransactions({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await apiClient.getPendingTransactions()
      commit('SET_PENDING_TRANSACTIONS', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching pending transactions:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },
  // Acciones específicas para egresos
  createEgreso({ commit }, egresoData) {
    commit('SET_LOADING', true)
    // Convertir datos de egreso al formato de transacción
    const transactionData = {
      reference: egresoData.reference || `EGR-${Date.now()}`,
      type: 'DISPATCH',
      status: 'Pendiente',
      supplier: egresoData.destination,  // El destino se envía como supplier
      notes: egresoData.notes,
      details: egresoData.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        source_location_id: egresoData.source_location_id
      }))
    }
    
    return apiClient.createTransaction(transactionData)
      .then(response => {
        const newTransaction = response.data.transaction || response.data
        commit('ADD_EGRESO', newTransaction)
        commit('SET_LOADING', false)
        return newTransaction
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  updateEgreso({ commit }, { id, data }) {
    commit('SET_LOADING', true)
    return apiClient.updateTransaction(id, data)
      .then(response => {
        const updatedTransaction = response.data.transaction || response.data
        commit('UPDATE_EGRESO', { id, ...updatedTransaction })
        commit('SET_LOADING', false)
        return updatedTransaction
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  deleteEgreso({ commit }, id) {
    commit('SET_LOADING', true)
    return apiClient.deleteTransaction(id)
      .then(response => {
        commit('REMOVE_EGRESO', id)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        return Promise.reject(error)
      })
  },
  // Acciones para órdenes
  fetchOrders({ commit }, filters = {}) {
    commit('SET_LOADING', true)
    return apiClient.getPickedOrders(filters)
      .then(response => {
        commit('SET_ORDERS', response.data)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching orders:', error)
        return Promise.reject(error)
      })
  },
  updateOrderStatus({ commit }, { orderId, status, additionalData = {} }) {
    const orderData = { status, ...additionalData }
    return apiClient.updateOrder(orderId, orderData)
      .then(response => {
        commit('UPDATE_ORDER', { id: orderId, ...response.data })
        return response.data
      })
      .catch(error => {
        console.error('Error updating order status:', error)
        return Promise.reject(error)
      })
  },
  // Acciones para consolidaciones
  fetchConsolidations({ commit }, filters = {}) {
    commit('SET_LOADING', true)
    return apiClient.getConsolidations(filters)
      .then(response => {
        commit('SET_CONSOLIDATIONS', response.data)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching consolidations:', error)
        return Promise.reject(error)
      })
  },
  createConsolidation({ commit, dispatch }, { orderIds, consolidationData }) {
    commit('SET_LOADING', true)
    return apiClient.consolidateOrders(orderIds, consolidationData)
      .then(response => {
        const newConsolidation = response.data
        commit('ADD_CONSOLIDATION', newConsolidation)
        
        // Actualizar estado de las órdenes a 'consolidada'
        orderIds.forEach(orderId => {
          commit('UPDATE_ORDER', { id: orderId, status: 'consolidada', consolidation_id: newConsolidation.id })
        })
        
        commit('SET_LOADING', false)
        return newConsolidation
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error creating consolidation:', error)
        return Promise.reject(error)
      })
  },
  assignConsolidationToDock({ commit }, { consolidationId, dockId }) {
    return apiClient.assignOrdersToDock(dockId, [consolidationId])
      .then(response => {
        commit('UPDATE_CONSOLIDATION', { id: consolidationId, assigned_dock: dockId })
        return response.data
      })
      .catch(error => {
        console.error('Error assigning consolidation to dock:', error)
        return Promise.reject(error)
      })
  },
  // Acciones para envíos
  fetchShipments({ commit }, filters = {}) {
    console.log('🚀 Store: fetchShipments called with filters:', filters)
    commit('SET_LOADING', true)
    return apiClient.getShipments(filters)
      .then(response => {
        console.log('📦 Store: API response received:', response)
        console.log('📊 Store: Shipments data:', response.data)
        console.log('📋 Store: Number of shipments:', response.data?.length || 0)
        
        // Commit the data to store
        commit('SET_SHIPMENTS', response.data)
        console.log('✅ Store: SET_SHIPMENTS mutation committed')
        
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('❌ Store: Error fetching shipments:', error)
        console.error('❌ Store: Error response:', error.response?.data)
        console.error('❌ Store: Error status:', error.response?.status)
        return Promise.reject(error)
      })
  },
  createShipment({ commit }, shipmentData) {
    commit('SET_LOADING', true)
    return apiClient.createDespachoShipment(shipmentData)
      .then(response => {
        const newShipment = response.data
        commit('ADD_SHIPMENT', newShipment)
        commit('SET_LOADING', false)
        return newShipment
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error creating shipment:', error)
        return Promise.reject(error)
      })
  },
  deleteShipment({ commit }, shipmentId) {
    commit('SET_LOADING', true)
    return apiClient.deleteShipment(shipmentId)
      .then(response => {
        commit('REMOVE_SHIPMENT', shipmentId)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error deleting shipment:', error)
        return Promise.reject(error)
      })
  },
  // Acciones para muelles
  fetchLoadingDocks({ commit }) {
    commit('SET_LOADING', true)
    return apiClient.getLoadingDocks()
      .then(response => {
        commit('SET_LOADING_DOCKS', response.data)
        commit('SET_LOADING', false)
        return response.data
      })
      .catch(error => {
        commit('SET_LOADING', false)
        console.error('Error fetching loading docks:', error)
        return Promise.reject(error)
      })
  },
  createLoadingDock({ commit }, dockData) {
    return apiClient.createLoadingDock(dockData)
      .then(response => {
        commit('ADD_LOADING_DOCK', response.data)
        return response.data
      })
      .catch(error => {
        console.error('Error creating loading dock:', error)
        return Promise.reject(error)
      })
  },
  updateLoadingDock({ commit }, { dockId, dockData }) {
    return apiClient.updateLoadingDock(dockId, dockData)
      .then(response => {
        commit('UPDATE_LOADING_DOCK', { id: dockId, ...response.data })
        return response.data
      })
      .catch(error => {
        console.error('Error updating loading dock:', error)
        return Promise.reject(error)
      })
  },
  startDockLoading({ commit }, { dockId, loadingData = {} }) {
    return apiClient.startDockLoading(dockId, loadingData)
      .then(response => {
        commit('UPDATE_LOADING_DOCK', { id: dockId, status: 'cargando' })
        return response.data
      })
      .catch(error => {
        console.error('Error starting dock loading:', error)
        return Promise.reject(error)
      })
  },
  completeDockLoading({ commit, dispatch }, { dockId, completionData = {} }) {
    return apiClient.completeDockLoading(dockId, completionData)
      .then(response => {
        // El backend debería retornar el envío creado
        const shipment = response.data.shipment
        if (shipment) {
          commit('ADD_SHIPMENT', shipment)
        }
        
        // Actualizar estado del muelle
        commit('UPDATE_LOADING_DOCK', { 
          id: dockId, 
          status: 'disponible', 
          current_load: 0, 
          assigned_orders: [] 
        })
        
        return response.data
      })
      .catch(error => {
        console.error('Error completing dock loading:', error)
        return Promise.reject(error)
      })
  },
  // Acciones para PDFs
  downloadShipmentManifest({ commit }, shipmentId) {
    return apiClient.downloadShipmentManifest(shipmentId)
      .then(response => {
        // Crear blob y descargar
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `manifiesto_${shipmentId}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        return response.data
      })
      .catch(error => {
        console.error('Error downloading PDF:', error)
        return Promise.reject(error)
      })
  },
  
  regenerateShipmentManifest({ commit }, shipmentId) {
    return apiClient.regenerateShipmentManifest(shipmentId)
      .then(response => {
        // Actualizar el shipment en el store
        commit('UPDATE_SHIPMENT', { id: shipmentId, ...response.data })
        return response.data
      })
      .catch(error => {
        console.error('Error regenerating PDF:', error)
        return Promise.reject(error)
      })
  }
}

const mutations = {
  SET_LOADING(state, status) {
    state.loading = status
  },
  SET_TRANSACTIONS(state, transactions) {
    state.transactions = transactions
  },
  SET_TRANSACTION(state, transaction) {
    state.transaction = transaction
  },
  ADD_TRANSACTION(state, transaction) {
    state.transactions.push(transaction)
  },
  UPDATE_TRANSACTION(state, updatedTransaction) {
    const index = state.transactions.findIndex(t => t.id === updatedTransaction.id)
    if (index !== -1) {
      // Hacemos un merge de la transacción existente con los datos nuevos
      state.transactions.splice(index, 1, { ...state.transactions[index], ...updatedTransaction })
    }
    if (state.transaction && state.transaction.id === updatedTransaction.id) {
      state.transaction = { ...state.transaction, ...updatedTransaction }
    }
  },
  SET_PENDING_TRANSACTIONS(state, transactions) {
    state.pendingTransactions = transactions
  },
  REMOVE_TRANSACTION(state, id) {
    state.transactions = state.transactions.filter(transaction => transaction.id !== id)
  },
  SET_EGRESOS(state, egresos) {
    state.egresos = egresos
  },
  ADD_EGRESO(state, egreso) {
    state.egresos.push(egreso)
  },
  UPDATE_EGRESO(state, updatedEgreso) {
    const index = state.egresos.findIndex(e => e.id === updatedEgreso.id)
    if (index !== -1) {
      state.egresos.splice(index, 1, { ...state.egresos[index], ...updatedEgreso })
    }
  },
  REMOVE_EGRESO(state, id) {
    state.egresos = state.egresos.filter(egreso => egreso.id !== id)
  },
  SET_ORDERS(state, orders) {
    state.orders = orders
  },
  UPDATE_ORDER(state, updatedOrder) {
    const index = state.orders.findIndex(o => o.id === updatedOrder.id)
    if (index !== -1) {
      state.orders.splice(index, 1, { ...state.orders[index], ...updatedOrder })
    }
  },
  SET_CONSOLIDATIONS(state, consolidations) {
    state.consolidations = consolidations
  },
  ADD_CONSOLIDATION(state, consolidation) {
    state.consolidations.push(consolidation)
  },
  UPDATE_CONSOLIDATION(state, updatedConsolidation) {
    const index = state.consolidations.findIndex(c => c.id === updatedConsolidation.id)
    if (index !== -1) {
      state.consolidations.splice(index, 1, { ...state.consolidations[index], ...updatedConsolidation })
    }
  },
  SET_SHIPMENTS(state, shipments) {
    console.log('🔧 Mutation: SET_SHIPMENTS called with:', shipments)
    console.log('🔧 Mutation: Previous shipments count:', state.shipments?.length || 0)
    console.log('🔧 Mutation: New shipments count:', shipments?.length || 0)
    
    state.shipments = shipments || []
    
    console.log('✅ Mutation: State updated. Current shipments count:', state.shipments.length)
  },
  ADD_SHIPMENT(state, shipment) {
    state.shipments.push(shipment)
  },
  UPDATE_SHIPMENT(state, updatedShipment) {
    const index = state.shipments.findIndex(s => s.id === updatedShipment.id)
    if (index !== -1) {
      state.shipments.splice(index, 1, { ...state.shipments[index], ...updatedShipment })
    }
  },
  REMOVE_SHIPMENT(state, shipmentId) {
    state.shipments = state.shipments.filter(shipment => shipment.id !== shipmentId)
  },
  SET_LOADING_DOCKS(state, loadingDocks) {
    state.loadingDocks = loadingDocks
  },
  ADD_LOADING_DOCK(state, loadingDock) {
    state.loadingDocks.push(loadingDock)
  },
  UPDATE_LOADING_DOCK(state, updatedLoadingDock) {
    const index = state.loadingDocks.findIndex(ld => ld.id === updatedLoadingDock.id)
    if (index !== -1) {
      state.loadingDocks.splice(index, 1, { ...state.loadingDocks[index], ...updatedLoadingDock })
    }
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
